import { Center } from '@assets/component/Center/Center';
import {
    RowEditor,
    RowEditorProps
} from '@assets/component/Financial/PanelCompanyYearly/PanelElements/RowEditor/RowEditor';
import { RowValue } from '@assets/component/Financial/PanelCompanyYearly/PanelElements/RowValue/RowValue';
import classNames from 'classnames';
import { observable, toJS } from 'mobx';
import { ReactElement, useEffect, useState } from 'react';
import * as style from './PanelTable.module.less';
import { useStore, useStoreUI } from '@assets/model/Store';
import { useSelectionStore } from '@assets/model/StoreSelection';
import { observer } from 'mobx-react';

export type TableField = {
    source: string;
    display: string;
    description?: string;
};

export type PanelColumn = {
    column: TableField;
    style?: {
        alignItems?: 'start' | 'end' | 'center' | 'stretch' | 'baseline';
        justifyContent?: 'start' | 'end' | 'center' | 'between' | 'around';
    };

    element?: (row: PanelRow, col: PanelColumn, data: any) => ReactElement;
    editor?: (api: RowEditorProps) => ReactElement;
    header?: (col: PanelColumn) => ReactElement;
    overrideValue?: (field: TableField, column: TableField) => any;
    overrideFormatting?: (data: any) => any;
    dontUseRowFormatting?: boolean;
};

export type PanelRow = {
    rowMetadata?: any;
    field: TableField;
    label?: (field: TableField) => ReactElement;
    element?: (row: PanelRow, col: PanelColumn, data: any) => ReactElement;
    editor?: (api: RowEditorProps) => ReactElement;
    formatter?: (data: any) => any;
    value?: (field: TableField, column: TableField) => any;
    onClick?: (row: PanelRow) => void;
    skipRow?: boolean;
    editable?: boolean;
    dontShowSources?: boolean;
};

interface IPanelTableProps {
    topLeftCellText?: string;
    noHeaderRow?: boolean;
    skipLabelInRow?: boolean;
    columns: PanelColumn[];
    rows: PanelRow[];
    disable_round_edges?: boolean;
    className?: string;
    onEditField?: (
        row: PanelRow,
        column: PanelColumn,
        newValue: any,
        rowIndex: number
    ) => void;
}

type CellId = {
    row: PanelRow;
    col: PanelColumn;
    rowIndex: number;
    colIndex: number;
};

export class EditTableStore {
    @observable accessor dealData: any = null;
    @observable accessor editCellValue: any = null;
    @observable accessor editCellId: CellId | null = null;
}

// Cell Selection Checkbox component
const SelectionCheckbox = observer(({ fieldName }: { fieldName: string }) => {
    const selectionStore = useSelectionStore();
    const isSelected = selectionStore.isFieldSelected(fieldName);
    
    // Force re-render on isSelectionModeActive changes
    const [, forceUpdate] = useState({});
    
    // Update when selection mode or selected fields change
    useEffect(() => {
        const timer = setInterval(() => {
            forceUpdate({});
        }, 100);
        
        return () => clearInterval(timer);
    }, []);
    
    // Log to help debug
    //console.log(`Rendering checkbox for ${fieldName}: isSelected=${isSelected}`);
    
    return (
        <input 
            type="checkbox" 
            checked={isSelected}
            onChange={() => {
                console.log(`Toggling selection for ${fieldName} from ${isSelected} to ${!isSelected}`);
                selectionStore.toggleSelection(fieldName);
                forceUpdate({});
            }}
            style={{ 
                margin: 0,
                position: 'absolute',
                left: '5px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 100,
                // Visual style to make it more noticeable
                width: '16px',
                height: '16px',
                accentColor: '#1976d2'
            }}
        />
    );
});

export const PanelTable = observer((props: IPanelTableProps) => {
    const [editValueStore] = useState(new EditTableStore());
    const [editCellRefresh, setEditCellRefreshUI] = useState<CellId | null>(
        null
    );
    
    const selectionStore = useSelectionStore();
    const isSelectionModeActive = selectionStore.isSelectionModeActive;
    const storeUI = useStoreUI();

    const skipHeadeRow = props.noHeaderRow === true;
    const skipRowLabel = props.skipLabelInRow === true;
    function onKeyDown(e: KeyboardEvent) {
        if (editValueStore.editCellId == null) return;

        if (e.key === 'Tab' || e.key == 'ArrowUp' || e.key == 'ArrowDown') {
            e.preventDefault();

            saveCurrentEdit();
            const columnsInRow = props.columns.length;

            const editCellId = editValueStore.editCellId;
            var nextColumnIndex = editCellId.colIndex;
            var nextRowIndex = editCellId.rowIndex;

            if (e.shiftKey) {
                nextColumnIndex -= 1;
                if (nextColumnIndex < 0) {
                    nextColumnIndex = 0;
                }
            } else if (e.key == 'ArrowUp') {
                for (let i = 0; i < rows.length; i++) {
                    nextRowIndex -= 1;

                    if (nextRowIndex <= -1) {
                        return;
                    }

                    if (props.rows[nextRowIndex].editable) {
                        break;
                    }
                }
            } else if (e.key == 'ArrowDown') {
                for (let i = 0; i < rows.length; i++) {
                    nextRowIndex += 1;
                    if (nextRowIndex >= props.rows.length) {
                        return;
                    }
                    if (props.rows[nextRowIndex].editable) {
                        break;
                    }
                }
            } else if (e.key == 'Tab') {
                nextColumnIndex += 1;
                if (nextColumnIndex >= columnsInRow) {
                    nextColumnIndex -= 1;
                }
            } else if (e.key == 'Enter') {
                setCellEditMode(null);
            }

            var newRow = props.rows[nextRowIndex];
            var newCol = props.columns[nextColumnIndex];
            setCellEditMode({
                row: newRow,
                col: newCol,
                rowIndex: nextRowIndex,
                colIndex: nextColumnIndex
            });
        } else if (e.key === 'Escape') {
            setCellEditMode(null);
        } else if (e.key === 'Delete') {
            const editCellId = editValueStore.editCellId;
            props.onEditField(
                editCellId.row,
                editCellId.col,
                null,
                editCellId.rowIndex
            );
            setCellEditMode(null);
        }
    }

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (editValueStore.editCellId) {
                const target = e.target as Element;
                // Check if click is outside the table or on a different cell
                const isInTable = target.closest('.grid_item');
                if (!isInTable || !target.closest('input')) {
                    saveCurrentEdit();
                    setCellEditMode(null);
                }
            }
        };

        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('click', handleClickOutside);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('click', handleClickOutside);
        };
    }, [editValueStore.editCellId, editValueStore.editCellValue]);

    const setCellEditMode = (cellId: CellId | null) => {
        const store = useStore();
        editValueStore.editCellId = cellId;
        setEditCellRefreshUI(editValueStore.editCellId);

        if (cellId == null) {
            setValueForEditCell('SHOULD_NOT_SEE');
            // Hide underlines when exiting edit mode
            storeUI.showEditedUnderlines = false;
            return;
        }

        editValueStore.dealData = toJS(store.query.activeReport);
        const valFn = cellId.col.overrideValue || cellId.row.value;
        let newEditValue = valFn(cellId.row.field, cellId.col.column);
        setValueForEditCell(newEditValue);
    };

    const saveCurrentEdit = () => {
        const val = editValueStore.editCellValue;
        const editCellId = editValueStore.editCellId;

        if (props.onEditField) {
            props.onEditField(
                editCellId.row,
                editCellId.col,
                val,
                editCellId.rowIndex
            );
        }
    };

    const setValueForEditCell = (value: any) => {
        if (
            value == null ||
            value.toString() == 'NaN' ||
            value.toString() == 'N\\A' ||
            value.toString() == 'N/A'
        ) {
            value = '';
        }

        editValueStore.editCellValue = value;
    };

    const disableRoundEdges = props.disable_round_edges === true;

    const elementsToGridRow = (
        row: number,
        rowData: PanelRow,
        elements: ReactElement[],
        classesToAdd: string[] = [],
        rowPosition: ('start' | 'end' | 'none')[] = ['none'],
        rowAttributes: Record<string, string> = {}
    ) => {
        return elements.map((el, index) => {
            var cls = classNames(
                style.grid_item,
                style[`grid_row_${row}`],
                style[`grid_col_${index + 1}`],
                `grid_row_${row}`,
                `grid_col_${index + 1}`,
                { even_row: row % 2 === 0 },
                { odd_row: row % 2 === 1 },
                ...classesToAdd,
                { [style.grid_col_start]: index === 0 },
                { [style.grid_col_end]: index === elements.length - 1 },
                { [style.grid_row_first]: rowPosition.includes('start') },
                { [style.grid_row_last]: rowPosition.includes('end') },
                { [style.disable_round_edges]: disableRoundEdges }
            );

            const cellAttributes = { ...rowAttributes };
            if (index === 0 && rowData && rowData.field?.source) {
                cellAttributes['data-field-name'] = rowData.field.source;
            }

            return (
                <div
                    key={'Row_' + row + '_' + index}
                    className={cls}
                    onClick={() => {
                        if (rowData && rowData.onClick && !rowData.editable) {
                            rowData.onClick(rowData);
                        }
                    }}
                    {...cellAttributes}
                >
                    {el}
                </div>
            );
        });
    };

    const rows = [];

    if (!skipHeadeRow) {
        const headerLabels = [
            <div>{props.topLeftCellText}</div>,
            ...props.columns.map(col => {
                const el = col.header ? (
                    col.header(col)
                ) : (
                    <div>{col.column.display}</div>
                );

                return <div key={col.column.source}>{el}</div>;
            })
        ];
        rows.push(elementsToGridRow(1, null, headerLabels, [style.headerRow]));
    }

    props.rows.forEach((row, rowIndex) => {
        if (row.skipRow) {
            return;
        }
        const newRow = [];
        if (!skipRowLabel) {
            // Render the label column with checkbox if in selection mode
            const labelContent = row.label ? row.label(row.field) : row.field.display;
            
            const labelWrapper = isSelectionModeActive ? (
                <div style={{ position: 'relative', paddingLeft: '25px' }}>
                    <SelectionCheckbox fieldName={row.field.source} />
                    {labelContent}
                </div>
            ) : labelContent;
            
            newRow.push(labelWrapper);
        }

        props.columns.forEach((col, colIndex) => {
            const isRowEditable = row.editable;
            const columnsInRow = props.columns.length;
            const cellId = { row, col, rowIndex, colIndex };
            const editCellId = editValueStore.editCellId;
            const editMode =
                editCellId &&
                editCellId.rowIndex == rowIndex &&
                editCellId.colIndex == colIndex;

            let val = '-';
            let val_noformatting = '-';
            const valFn = col.overrideValue || row.value;
            try {
                val = valFn(row.field, col.column);
                val_noformatting = val;

                if (!editMode) {
                    if (col.overrideFormatting) {
                        val = col.overrideFormatting(val);
                    } else if (
                        row.formatter &&
                        col.dontUseRowFormatting !== true
                    ) {
                        val = row.formatter(val);
                    }
                }
            } catch (e) {
                console.log('Error in PanelTable', e);
                val = '-';
            }

            const createEl = col.element || row.element;
            const createEditor = col.editor || row.editor || RowEditor.textbox;

            const onEditApi = (value: string, save: boolean) => {
                setValueForEditCell(value);
                if (save) {
                    saveCurrentEdit();
                    setCellEditMode(null);
                }
            };
            const editApi: RowEditorProps = {
                row,
                col,
                val: editValueStore,
                onEdit: onEditApi
            };
            const justifyContent = col?.style?.justifyContent || 'center';
            const alignItems = col?.style?.alignItems || 'center';

            const newEl = (
                <div
                    key={colIndex}
                    style={{ 
                        width: '100%', 
                        height: '100%',
                        display: 'flex',
                        justifyContent: justifyContent,
                        alignItems: alignItems
                    }}
                                        onDoubleClick={(e) => {
                        if (props.onEditField && row.editable && !editMode) {
                            // Don't make Source columns editable
                            const createEl = col.element || row.element;
                            const isSourceColumn = createEl === RowValue.sources;
                            if (!isSourceColumn) {
                                e.stopPropagation();
                                storeUI.showEditedUnderlines = true;
                            setCellEditMode({ row, col, rowIndex, colIndex });
                            }
                        }
                    }}
                >
                    {editMode ? createEditor(editApi) : createEl(row, col, val)}
                </div>
            );
            
            newRow.push(newEl);
        });

        const rowPosition = [];
        const isFirstRow = rowIndex === 0;
        if (isFirstRow) {
            rowPosition.push('start');
        }
        const isLastRow = rowIndex === props.rows.length - 1;
        if (isLastRow) {
            rowPosition.push('end');
        }

        const rowAttributes = {};

        rows.push(
            elementsToGridRow(rows.length + 1, row, newRow, [], rowPosition, rowAttributes)
        );
    });

    var cls = classNames(style.gridContainer, props.className);
    return <div className={cls}>{rows}</div>;
});
